import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import type { FacilityLicence, FacilityLicenceDetail, FacilityLicencePublic, IssuedDocRef } from '../../../lib/types';
import { useApiQuery } from '../../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../../ui/components';

// A separate role from case management entirely — verified against
// m27-family-affairs/src/service/caseAccess.ts's FACILITY_INSPECTOR_ROLES.
// A SOCIAL_WORKER/SENIOR_SW has zero access to the licensing console (only
// the public register below), and vice versa.
const INSPECTOR_ROLES = ['FACILITY_INSPECTOR'];

/**
 * Facility licensing + inspections — GET/POST
 * /api/v1/m27/facility-licences/*. Application intake (POST
 * /facility-licences) is operator self-service (verified NIN, optional
 * X-Acting-For mandate) — no staff-side "apply on behalf of" endpoint, so
 * this screen covers inspection, staff vetting, and the licensing decision,
 * which is what FACILITY_INSPECTOR actually owns. The public register
 * (names/type/district/status only, T1+, no role required) is shown to
 * every signed-in staff persona regardless of role.
 */
export function Facilities() {
  const { persona } = useAuth();
  const isInspector = persona?.roles.some((r) => INSPECTOR_ROLES.includes(r)) ?? false;

  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [publicQ2, setPublicQ2] = useState('');

  const query = useMemo(() => {
    const p = new URLSearchParams({ limit: '50' });
    if (statusFilter) p.set('status', statusFilter);
    if (q) p.set('q', q);
    return `/api/v1/m27/facility-licences?${p}`;
  }, [statusFilter, q]);
  const listQ = useApiQuery<{ items: FacilityLicence[]; nextCursor: string | null }>(isInspector ? query : null);

  const publicQuery = useMemo(() => {
    const p = new URLSearchParams({ limit: '50' });
    if (publicQ2) p.set('q', publicQ2);
    return `/api/v1/m27/facility-licences/public?${p}`;
  }, [publicQ2]);
  const publicListQ = useApiQuery<{ items: FacilityLicencePublic[]; nextCursor: string | null }>(publicQuery);

  return (
    <div>
      <PageHeader
        title="Facility Licensing & Inspections"
        subtitle="Daycares, childminders and elderly homes — application intake is operator self-service; inspection, vetting and licensing are done here"
      />

      {isInspector ? (
        <div className="mb-8">
          <h2 className="mb-2 text-sm font-bold text-slate-700">Licensing console (Facility Inspector)</h2>
          <div className="mb-4 flex flex-wrap gap-3">
            <input className="field-input max-w-xs" placeholder="Search facility or ref…" value={q} onChange={(e) => setQ(e.target.value)} />
            <select className="field-input max-w-[180px]" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All statuses</option>
              <option value="APPLIED">Applied</option>
              <option value="UNDER_REVIEW">Under review</option>
              <option value="LICENSED">Licensed</option>
              <option value="SUSPENDED">Suspended</option>
              <option value="REVOKED">Revoked</option>
              <option value="REJECTED">Rejected</option>
              <option value="EXPIRED">Expired</option>
            </select>
          </div>
          <div className="card overflow-x-auto">
            {listQ.loading ? (
              <Spinner />
            ) : listQ.error ? (
              <ErrorNote message={listQ.error} />
            ) : (listQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No facility licences found" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Ref</th>
                    <th className="table-head">Facility</th>
                    <th className="table-head">Type</th>
                    <th className="table-head">District</th>
                    <th className="table-head">Status</th>
                    <th className="table-head" />
                  </tr>
                </thead>
                <tbody>
                  {listQ.data!.items.map((f) => (
                    <tr key={f.id} className="hover:bg-slate-50">
                      <td className="table-cell font-medium">{f.refNo}</td>
                      <td className="table-cell text-xs">{f.facilityName}</td>
                      <td className="table-cell text-xs">{f.facilityType.replaceAll('_', ' ')}</td>
                      <td className="table-cell text-xs">{f.district.replaceAll('-', ' ')}</td>
                      <td className="table-cell">
                        <StatusBadge status={f.status} />
                      </td>
                      <td className="table-cell text-right">
                        <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setOpenId(f.id)}>
                          Open
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : (
        <div className="mb-6">
          <ErrorNote message="The licensing console requires the Facility Inspector role — showing the public register below." />
        </div>
      )}

      <div>
        <h2 className="mb-2 text-sm font-bold text-slate-700">Public register — licensed facilities (names only)</h2>
        <input className="field-input mb-3 max-w-xs" placeholder="Search facility name…" value={publicQ2} onChange={(e) => setPublicQ2(e.target.value)} />
        <div className="card overflow-x-auto">
          {publicListQ.loading ? (
            <Spinner />
          ) : publicListQ.error ? (
            <ErrorNote message={publicListQ.error} />
          ) : (publicListQ.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="No licensed facilities found" />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Facility</th>
                  <th className="table-head">Type</th>
                  <th className="table-head">District</th>
                  <th className="table-head">Status</th>
                </tr>
              </thead>
              <tbody>
                {publicListQ.data!.items.map((f) => (
                  <tr key={f.id}>
                    <td className="table-cell font-medium">{f.facilityName}</td>
                    <td className="table-cell text-xs">{f.facilityType.replaceAll('_', ' ')}</td>
                    <td className="table-cell text-xs">{f.district.replaceAll('-', ' ')}</td>
                    <td className="table-cell">
                      <StatusBadge status={f.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {openId ? <FacilityDetailModal id={openId} onClose={() => setOpenId(null)} onChanged={() => { listQ.reload(); publicListQ.reload(); }} /> : null}
    </div>
  );
}

function FacilityDetailModal({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const detailQ = useApiQuery<FacilityLicenceDetail>(`/api/v1/m27/facility-licences/${id}`);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [issuedCert, setIssuedCert] = useState<IssuedDocRef | null>(null);

  const refresh = () => {
    detailQ.reload();
    onChanged();
  };

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      refresh();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  }

  // inspections
  const [showAddInspection, setShowAddInspection] = useState(false);
  const [inspectedAt, setInspectedAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [outcome, setOutcome] = useState('PASS');
  const [findings, setFindings] = useState('');
  const [followUpDue, setFollowUpDue] = useState('');

  // vetting
  const [showAddVetting, setShowAddVetting] = useState(false);
  const [vSubjectNin, setVSubjectNin] = useState('');
  const [vSubjectName, setVSubjectName] = useState('');
  const [vSerial, setVSerial] = useState('');
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [verifyHint, setVerifyHint] = useState<'CLEAN' | 'RECORD_DISCLOSED'>('CLEAN');
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [decideChoice, setDecideChoice] = useState<'ACCEPT' | 'UNSUITABLE'>('ACCEPT');
  const [decideReason, setDecideReason] = useState('');

  // decision
  const [reasonInput, setReasonInput] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [showSuspend, setShowSuspend] = useState(false);
  const [showRevoke, setShowRevoke] = useState(false);
  const [validForDays, setValidForDays] = useState('365');

  const data = detailQ.data;

  const license = () =>
    run(async () => {
      const res = await api.post<FacilityLicence & { certificate?: IssuedDocRef }>(`/api/v1/m27/facility-licences/${id}/decision`, {
        decision: 'LICENSE',
        validForDays: Number.parseInt(validForDays, 10),
      });
      if (res.certificate) setIssuedCert(res.certificate);
    });

  return (
    <Modal title={data ? `Facility — ${data.refNo}` : 'Facility licence'} onClose={onClose} wide>
      {detailQ.loading ? (
        <Spinner />
      ) : detailQ.error ? (
        <ErrorNote message={detailQ.error} />
      ) : data ? (
        <div className="space-y-5">
          {error ? <ErrorNote message={error} /> : null}
          {issuedCert ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
              Certificate issued — serial <strong>{issuedCert.serial}</strong>.{' '}
              <a className="underline" href={issuedCert.verifyUrl} target="_blank" rel="noopener noreferrer">
                Verify
              </a>
            </div>
          ) : null}

          <div>
            <StatusBadge status={data.status} />
            <div className="mt-1 text-lg font-bold text-slate-900">{data.facilityName}</div>
            <div className="text-xs text-slate-500">
              {data.facilityType.replaceAll('_', ' ')} · {data.address} · District {data.district.replaceAll('-', ' ')} · Capacity {data.capacity}
            </div>
            <div className="mt-1 flex flex-wrap gap-1 text-xs text-slate-500">
              {data.ratios.map((r, i) => (
                <span key={i} className="rounded bg-slate-100 px-1.5 py-0.5">
                  {r.ageBand}: {r.ratio}
                </span>
              ))}
            </div>
            {data.rejectReason ? <ErrorNote message={`Rejected: ${data.rejectReason}`} /> : null}
            {data.suspendReason ? <p className="mt-1 text-xs text-amber-600">Suspended: {data.suspendReason}</p> : null}
            {data.revokeReason ? <p className="mt-1 text-xs text-rose-600">Revoked: {data.revokeReason}</p> : null}
            {data.docVaultSerial ? (
              <p className="mt-1 text-xs text-slate-400">
                Certificate {data.docVaultSerial}
                {data.expiresAt ? ` · expires ${formatDate(data.expiresAt)}` : ''}
              </p>
            ) : null}
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-700">Inspections</h3>
              <button className="btn-secondary !px-2 !py-0.5 text-xs" onClick={() => setShowAddInspection((v) => !v)}>
                + Record
              </button>
            </div>
            {data.inspections.length === 0 ? (
              <EmptyState title="No inspections recorded" />
            ) : (
              <ul className="space-y-1.5">
                {data.inspections.map((i) => (
                  <li key={i.id} className="rounded-lg border border-slate-200 p-2.5 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{formatDateTime(i.inspectedAt)}</span>
                      <StatusBadge status={i.outcome} />
                    </div>
                    <p className="text-xs text-slate-600">{i.findings}</p>
                    {i.followUpDue ? <p className="text-xs text-slate-400">Follow-up due {formatDate(i.followUpDue)}</p> : null}
                  </li>
                ))}
              </ul>
            )}
            {showAddInspection ? (
              <div className="mt-2 space-y-2 rounded-lg border border-slate-200 p-3">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Inspected on">
                    <input className="field-input" type="date" value={inspectedAt} onChange={(e) => setInspectedAt(e.target.value)} />
                  </Field>
                  <Field label="Outcome">
                    <select className="field-input" value={outcome} onChange={(e) => setOutcome(e.target.value)}>
                      <option value="PASS">Pass</option>
                      <option value="MINOR_ISSUES">Minor issues</option>
                      <option value="MAJOR_ISSUES">Major issues</option>
                      <option value="FAIL">Fail</option>
                    </select>
                  </Field>
                </div>
                <Field label="Findings">
                  <textarea className="field-input" rows={2} value={findings} onChange={(e) => setFindings(e.target.value)} />
                </Field>
                <Field label="Follow-up due (if any)">
                  <input className="field-input" type="date" value={followUpDue} onChange={(e) => setFollowUpDue(e.target.value)} />
                </Field>
                <div className="flex justify-end">
                  <button
                    className="btn-primary !py-1 text-xs"
                    disabled={busy || findings.length < 5}
                    onClick={() => run(async () => {
                      await api.post(`/api/v1/m27/facility-licences/${id}/inspections`, {
                        inspectedAt: new Date(inspectedAt).toISOString(),
                        outcome,
                        findings,
                        followUpDue: followUpDue || undefined,
                      });
                      setFindings(''); setFollowUpDue(''); setShowAddInspection(false);
                    })}
                  >
                    Record inspection
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-700">Staff vetting checks</h3>
              <button className="btn-secondary !px-2 !py-0.5 text-xs" onClick={() => setShowAddVetting((v) => !v)}>
                + Add
              </button>
            </div>
            {data.vettingChecks.length === 0 ? (
              <EmptyState title="No staff vetting checks recorded" />
            ) : (
              <ul className="space-y-1.5">
                {data.vettingChecks.map((v) => (
                  <li key={v.id} className="rounded-lg border border-slate-200 p-2.5 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{v.subjectName}</span>
                      <StatusBadge status={v.result} />
                    </div>
                    {v.notes ? <p className="whitespace-pre-wrap text-xs text-slate-500">{v.notes}</p> : null}
                    {v.result === 'PENDING' && v.clearanceCertSerial ? (
                      verifyingId === v.id ? (
                        <div className="mt-1.5 flex items-center gap-1.5">
                          <select className="field-input !py-1 text-xs" value={verifyHint} onChange={(e) => setVerifyHint(e.target.value as typeof verifyHint)}>
                            <option value="CLEAN">Certificate states CLEAN</option>
                            <option value="RECORD_DISCLOSED">Certificate states RECORD_DISCLOSED</option>
                          </select>
                          <button
                            className="btn-primary !px-2 !py-0.5 text-xs"
                            disabled={busy}
                            onClick={() => run(async () => {
                              await api.post(`/api/v1/m27/facility-licences/${id}/vetting-checks/${v.id}/verify`, { resultHint: verifyHint });
                              setVerifyingId(null);
                            })}
                          >
                            Verify
                          </button>
                        </div>
                      ) : (
                        <button className="btn-secondary mt-1.5 !px-2 !py-0.5 text-xs" onClick={() => setVerifyingId(v.id)}>
                          Verify against DocVault
                        </button>
                      )
                    ) : v.result === 'PENDING' ? (
                      <p className="mt-1 text-xs text-slate-400">No clearance certificate serial on file yet.</p>
                    ) : null}
                    {v.result === 'DISCLOSED' ? (
                      decidingId === v.id ? (
                        <div className="mt-1.5 space-y-1.5">
                          <select className="field-input !py-1 text-xs" value={decideChoice} onChange={(e) => setDecideChoice(e.target.value as typeof decideChoice)}>
                            <option value="ACCEPT">Accept — not disqualifying</option>
                            <option value="UNSUITABLE">Unsuitable — disqualifying</option>
                          </select>
                          <input className="field-input !py-1 text-xs" placeholder="Reason" value={decideReason} onChange={(e) => setDecideReason(e.target.value)} />
                          <button
                            className="btn-primary !px-2 !py-0.5 text-xs"
                            disabled={busy || decideReason.length < 5}
                            onClick={() => run(async () => {
                              await api.post(`/api/v1/m27/facility-licences/${id}/vetting-checks/${v.id}/decide`, { decision: decideChoice, reason: decideReason });
                              setDecidingId(null); setDecideReason('');
                            })}
                          >
                            Record suitability decision
                          </button>
                        </div>
                      ) : (
                        <button className="btn-secondary mt-1.5 !px-2 !py-0.5 text-xs" onClick={() => setDecidingId(v.id)}>
                          Record suitability decision
                        </button>
                      )
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {showAddVetting ? (
              <div className="mt-2 space-y-2 rounded-lg border border-slate-200 p-3">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Staff NIN">
                    <input className="field-input" value={vSubjectNin} onChange={(e) => setVSubjectNin(e.target.value)} />
                  </Field>
                  <Field label="Staff name">
                    <input className="field-input" value={vSubjectName} onChange={(e) => setVSubjectName(e.target.value)} />
                  </Field>
                </div>
                <Field label="Clearance cert serial (if presented)">
                  <input className="field-input" value={vSerial} onChange={(e) => setVSerial(e.target.value)} />
                </Field>
                <div className="flex justify-end">
                  <button
                    className="btn-primary !py-1 text-xs"
                    disabled={busy || !vSubjectNin || !vSubjectName}
                    onClick={() => run(async () => {
                      await api.post(`/api/v1/m27/facility-licences/${id}/vetting-checks`, { subjectNin: vSubjectNin, subjectName: vSubjectName, clearanceCertSerial: vSerial || undefined });
                      setVSubjectNin(''); setVSubjectName(''); setVSerial(''); setShowAddVetting(false);
                    })}
                  >
                    Add vetting check
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div>
            <h3 className="mb-1 text-sm font-bold text-slate-700">Licensing decision</h3>
            <div className="flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 p-3">
              {(['APPLIED', 'UNDER_REVIEW'] as string[]).includes(data.status) ? (
                <>
                  <Field label="Valid for (days)">
                    <input className="field-input w-24" type="number" value={validForDays} onChange={(e) => setValidForDays(e.target.value)} />
                  </Field>
                  <button className="btn-success !py-2" disabled={busy} onClick={license}>
                    License
                  </button>
                  <button className="btn-danger !py-2" onClick={() => setShowReject((v) => !v)}>
                    Reject
                  </button>
                </>
              ) : null}
              {data.status === 'LICENSED' ? (
                <button className="btn-secondary !py-2" onClick={() => setShowSuspend((v) => !v)}>
                  Suspend
                </button>
              ) : null}
              {data.status === 'SUSPENDED' ? (
                <>
                  <button
                    className="btn-secondary !py-2"
                    disabled={busy}
                    onClick={() => run(async () => { await api.post(`/api/v1/m27/facility-licences/${id}/decision`, { decision: 'REINSTATE' }); })}
                  >
                    Reinstate
                  </button>
                  <button className="btn-danger !py-2" onClick={() => setShowRevoke((v) => !v)}>
                    Revoke
                  </button>
                </>
              ) : null}
              {data.status === 'LICENSED' ? (
                <button className="btn-danger !py-2" onClick={() => setShowRevoke((v) => !v)}>
                  Revoke
                </button>
              ) : null}
            </div>
            {showReject || showSuspend || showRevoke ? (
              <div className="mt-2 flex items-end gap-2 rounded-lg border border-slate-200 p-3">
                <Field label="Reason" className="flex-1">
                  <input className="field-input" value={reasonInput} onChange={(e) => setReasonInput(e.target.value)} />
                </Field>
                <button
                  className="btn-danger !py-2"
                  disabled={busy || reasonInput.length < 5}
                  onClick={() => run(async () => {
                    const decision = showReject ? 'REJECT' : showSuspend ? 'SUSPEND' : 'REVOKE';
                    await api.post(`/api/v1/m27/facility-licences/${id}/decision`, { decision, reason: reasonInput });
                    setReasonInput(''); setShowReject(false); setShowSuspend(false); setShowRevoke(false);
                  })}
                >
                  Confirm {showReject ? 'reject' : showSuspend ? 'suspend' : 'revoke'}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
